import { useEffect, useMemo, useState } from "react";
import api, { inr } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { Search, Plus, Minus, Trash2, Clock, Leaf, Sparkles, Gift, Shirt, Wheat, GraduationCap, Package } from "lucide-react";

// Same 6 canonical categories as public storefront / admin editor / IBA shop.
const CATS = [
  { key: "all",          label: "All",                icon: Package     },
  { key: "health",       label: "Health & Wellness",  icon: Leaf        },
  { key: "personal_care",label: "Personal Care",      icon: Sparkles    },
  { key: "combo",        label: "Combos",             icon: Gift        },
  { key: "fashion",      label: "Fashion & Apparel",  icon: Shirt       },
  { key: "agriculture",  label: "Agriculture",        icon: Wheat       },
  { key: "education",    label: "Education",          icon: GraduationCap},
];

export default function FranchisePos() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Fetch product catalog once on mount — api is a stable module import
    // and setItems is a stable React setter, so an empty dep array is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/products?limit=200").then((r) => setItems(r.data.items || []));
  }, []);

  const visible = useMemo(() => {
    let out = items.filter((p) => (p.sku || "").startsWith("SW-"));
    if (cat !== "all") out = out.filter((p) => p.category === cat);
    const term = q.trim().toLowerCase();
    if (term) {
      out = out.filter((p) => {
        const hay = [p.name, p.sku, p.description, p.category].filter(Boolean).join(" ").toLowerCase();
        return term.split(/\s+/).every((t) => hay.includes(t));
      });
    }
    // LIVE first, Coming Soon at the end (still visible but non-clickable)
    return [...out].sort((a, b) => (a.is_coming_soon ? 1 : 0) - (b.is_coming_soon ? 1 : 0));
  }, [items, cat, q]);

  const add = (p) => {
    if (p.is_coming_soon) { toast.info(`${p.name} — Coming Soon (cannot be sold)`); return; }
    setCart((prev) => {
      const ex = prev.find((i) => i.product_id === p.id);
      if (ex) return prev.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { product_id: p.id, sku: p.sku, name: p.name, mrp: p.mrp, dp: p.dp, bv: p.sp, qty: 1, image: p.image }];
    });
  };
  const setQty = (id, qty) => setCart((p) => p.map((i) => i.product_id === id ? { ...i, qty: Math.max(1, qty) } : i));
  const remove = (id) => setCart((p) => p.filter((i) => i.product_id !== id));

  const subtotal = cart.reduce((s, i) => s + i.dp * i.qty, 0);
  const totalBv = cart.reduce((s, i) => s + i.sp * i.qty, 0);

  const checkout = async () => {
    if (cart.length === 0) { toast.error("Add items first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/franchise/pos-sale", { items: cart, shipping_address: { walk_in: true }, payment_mode: "cash" });
      toast.success(`POS sale ${data.order_no} · Commission ${inr(data.commission)}`);
      setCart([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout title="POS Billing" subtitle="Walk-in customer sale · Coming Soon items are preview-only">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-sm">
          <div className="p-4 border-b border-border space-y-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
              <input
                data-testid="pos-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, SKU, description…"
                className="w-full border border-border pl-9 pr-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none"
              />
            </div>
            {/* Canonical category tabs */}
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="pos-cats">
              {CATS.map((c) => {
                const Icon = c.icon;
                return (
                  <button
                    key={c.key}
                    onClick={() => setCat(c.key)}
                    data-testid={`pos-cat-${c.key}`}
                    className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm border transition-colors inline-flex items-center gap-1.5 ${
                      cat === c.key
                        ? "bg-royal text-gold border-royal"
                        : "bg-white text-navy/70 border-line hover:border-gold"
                    }`}
                  >
                    <Icon size={11} /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[600px] overflow-y-auto">
            {visible.length === 0 && (
              <div className="col-span-full py-10 text-center text-sm text-navy/50">No products match this filter.</div>
            )}
            {visible.map((p) => {
              const isCS = !!p.is_coming_soon;
              return (
                <button
                  key={p.id}
                  data-testid={`pos-add-${p.id}`}
                  onClick={() => add(p)}
                  disabled={isCS}
                  title={isCS ? "Coming Soon — cannot be sold at POS" : `Add ${p.name} to sale`}
                  className={`relative text-left p-3 border rounded-sm transition-colors ${
                    isCS
                      ? "border-line opacity-60 cursor-not-allowed"
                      : "border-line hover:border-gold"
                  }`}
                >
                  <div className="relative">
                    <img
                      src={p.image
                        ? `${p.image}${p.image.includes("?") ? "&" : "?"}v=${p.updated_at || p.id}`
                        : "/product-images/coming-soon.svg"}
                      alt=""
                      onError={(e) => { e.currentTarget.src = "/product-images/coming-soon.svg"; }}
                      className={`w-full aspect-square object-cover rounded-sm bg-cream mb-2 ${isCS ? "grayscale" : ""}`}
                    />
                    {isCS && (
                      <span className="absolute inset-x-0 bottom-2 bg-royal/95 text-gold text-[9px] font-semibold uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1">
                        <Clock size={9} /> Coming Soon
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-navy/50 font-mono">{p.sku}</div>
                  <div className="text-sm font-medium text-navy line-clamp-2">{p.name}</div>
                  <div className="mt-1 flex justify-between text-xs">
                    <span className="text-navy font-semibold">{inr(p.dp)}</span>
                    {!isCS && <span className="text-gold-deep">{p.sp} SP</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <aside className="bg-white border border-border rounded-sm h-fit sticky top-24">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-serif text-xl text-navy">Cart</h3>
          </div>
          <ul className="divide-y divide-border max-h-[420px] overflow-y-auto">
            {cart.length === 0 && <li className="p-6 text-sm text-navy/50 text-center">Cart empty</li>}
            {cart.map((i) => (
              <li key={i.product_id} className="p-3 flex items-center gap-3">
                <img
                  src={i.image || "/product-images/coming-soon.svg"}
                  alt=""
                  onError={(e) => { e.currentTarget.src = "/product-images/coming-soon.svg"; }}
                  className="w-10 h-10 object-cover bg-cream rounded-sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-navy truncate">{i.name}</div>
                  <div className="text-xs text-navy/50">{inr(i.dp)} · {i.sp} SP</div>
                </div>
                <div className="flex items-center border border-border rounded-sm">
                  <button data-testid={`pos-minus-${i.product_id}`} onClick={() => setQty(i.product_id, i.qty - 1)} className="px-2 py-1"><Minus size={12} /></button>
                  <span className="px-2 text-xs">{i.qty}</span>
                  <button data-testid={`pos-plus-${i.product_id}`} onClick={() => setQty(i.product_id, i.qty + 1)} className="px-2 py-1"><Plus size={12} /></button>
                </div>
                <button data-testid={`pos-del-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-rose p-1"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
          <div className="p-5 border-t border-border space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-navy/60">Subtotal (DP)</span><span>{inr(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-navy/60">Total SP</span><span className="text-gold-deep font-medium">{totalBv}</span></div>
            <div className="border-t border-border pt-2 flex justify-between items-baseline"><span className="font-serif text-xl">Total</span><span data-testid="pos-total" className="text-xl text-navy font-extrabold tabular-nums lining-nums">{inr(subtotal)}</span></div>
            <button data-testid="pos-checkout" disabled={busy || cart.length === 0} onClick={checkout} className="w-full mt-3 bg-gold text-navy py-3 rounded-sm font-medium disabled:opacity-50">
              {busy ? "Processing..." : "Process Sale"}
            </button>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}
